import { z } from 'zod';
import {
  EMPTY_FIELD_MESSAGE,
  validateEditorEn,
  validateSpace,
  validateSpaceTextInput,
  validateTextEn,
} from '@Utils/validation';
import {
  OTHER_INCOME_STRUCTURE_ID,
  OTHER_INCOME_TYPE_ID,
} from 'src/Enum/IncomeStructure';
import { uploadImageSchema } from '@Containers/UploadImageFormFields/validators';
import { IIncomeItemFormValues } from '../NatureOfBusinessFormProvider/types';
import { yearFields } from './utils';

enum INCOME_STRUCTURE_MESSAGE {
  INTRODUCTION_EN = 'กรุณาระบุคำบรรยายเป็นภาษาอังกฤษ',
  REMARK_EN = 'กรุณาระบุหมายเหตุเป็นภาษาอังกฤษ',
  NEGATIVE_NUMBER = 'ไม่สามารถระบุค่าติดลบได้',
  NONE_VALUE = 'กรุณาระบุ',
  ASSETS = 'กรุณาเลือกไฟล์ภาพสายผลิตภัณฑ์หรือกลุ่มธุรกิจ',
  DESCRIPTION_ASSETS_EN = 'กรุณาระบุชื่อรูปภาพ/หัวข้อ/คำอธิบายเพิ่มเติมเป็นภาษาอังกฤษ',
}

export const IntroductionSchema = z.object({
  descriptionTh: z
    .string()
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  descriptionEn: z
    .string()
    .regex(validateEditorEn, INCOME_STRUCTURE_MESSAGE.INTRODUCTION_EN)
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
});

export const RemarkSchema = z.object({
  descriptionTh: z
    .string()
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  descriptionEn: z
    .string()
    .regex(validateTextEn, INCOME_STRUCTURE_MESSAGE.REMARK_EN)
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
});

const AssetSchema = uploadImageSchema({
  assets: INCOME_STRUCTURE_MESSAGE.ASSETS,
  descriptionAssetsEn: INCOME_STRUCTURE_MESSAGE.DESCRIPTION_ASSETS_EN,
});

const IncomeNumberSchema = z.string({
  message: INCOME_STRUCTURE_MESSAGE.NONE_VALUE,
});

export const IncomeCategorySchema = z.object({
  nameTh: z.string().min(1).refine(validateSpaceTextInput),
  nameEn: z
    .string()
    .min(1)
    .regex(validateTextEn)
    .refine(validateSpaceTextInput),
});

const IncomeItemSchema = z.object({
  asOfYear: IncomeNumberSchema.optional(),
  asOfYesteryear: IncomeNumberSchema.optional(),
  asOfYearBeforeYesteryear: IncomeNumberSchema.optional(),
});

const optionalKeys = new Set([
  OTHER_INCOME_STRUCTURE_ID.OTHER_PRODUCT_INCOME.toString(),
  OTHER_INCOME_TYPE_ID.SHARE_PROFIT.toString(),
  OTHER_INCOME_STRUCTURE_ID.OTHER_EXPORT_INCOME.toString(),
]);

const negativeAllowedKeys = new Set([
  OTHER_INCOME_TYPE_ID.SHARE_PROFIT.toString(),
]);

const validateIncomeItem = (
  key: string,
  value: IIncomeItemFormValues,
  ctx: z.RefinementCtx
) => {
  if (optionalKeys.has(key)) return;

  yearFields.forEach((field) => {
    if (!value[field]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: INCOME_STRUCTURE_MESSAGE.NONE_VALUE,
        path: [key, field],
      });
    }
  });
};

const validateNegativeValue = (
  key: string,
  value: IIncomeItemFormValues,
  ctx: z.RefinementCtx
) => {
  if (negativeAllowedKeys.has(key)) return;

  yearFields.forEach((field) => {
    if (Number(value[field]) < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: INCOME_STRUCTURE_MESSAGE.NEGATIVE_NUMBER,
        path: [key, field],
      });
    }
  });
};

const IncomeStructureItemsSchema = z
  .record(z.string(), IncomeItemSchema)
  .superRefine((data, ctx) => {
    Object.entries(data).forEach(([key, value]) => {
      validateIncomeItem(key, value, ctx);
      validateNegativeValue(key, value, ctx);
    });
  });

export const IncomeStructureSchema = z.object({
  introduction: IntroductionSchema.optional().nullable(),
  remark: RemarkSchema.optional().nullable(),
  productIncomeItems: IncomeStructureItemsSchema,
  productIncomePercentages: IncomeStructureItemsSchema,
  exportIncomeItems: IncomeStructureItemsSchema,
  exportIncomePercentages: IncomeStructureItemsSchema,
  otherIncomeItems: IncomeStructureItemsSchema,
  assets: z.array(AssetSchema).optional().nullable(),
});
